import { ErrorBoundary } from '@ctw/shared/components/errors/error-boundary';
import { Modal, type ModalProps } from '@ctw/shared/components/modal';
import { LoadTrackingProvider } from '@ctw/shared/context/load-tracking-context';
import {
  type TelemetryContext,
  useTelemetry,
} from '@ctw/shared/context/telemetry/telemetry-boundary';
import { TIMER_MODAL_LOAD_TIME } from '@ctw/shared/context/telemetry/timer-metrics';
import {
  type OverlayLoadTrigger,
  useOverlayLoadMetric,
} from '@ctw/shared/context/use-overlay-load-metric';
import { pick } from 'lodash-es';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { SetOptional } from 'type-fest';

export interface OpenModalAction extends Pick<ModalProps, 'label' | 'title' | 'content'> {
  telemetryContext?: TelemetryContext;
  onModalEvent?: (eventType: ModalEventType) => void;
  /* When the user's request to open this modal began, for modal load time. Defaults to the moment
   * the modal opens; pass it explicitly when work the user is waiting on (such as fetching the
   * resource being opened) ran first.
   */
  loadStartedAt?: number;
  loadTrigger?: OverlayLoadTrigger;
}

/* Context properties naming the resource a modal was opened for, carried onto its load time
 * metric. Set by `ResourceModalProvider`; absent for modals that aren't about a resource.
 */
const MODAL_RESOURCE_TELEMETRY_CONTEXT_KEYS = ['modalResourceType', 'modalResourceId'] as const;

type ModalEventType = 'open' | 'afterOpen' | 'close' | 'afterClose';

export type ModalEventHandler = (eventType: ModalEventType) => void;

export interface ModalBoundary {
  name: string;
  telemetry: ReturnType<typeof useTelemetry>;
  context?: TelemetryContext;
}

type ModalLayoutMode = 'fit' | 'full';
type ModalLayoutOverflowBehavior = 'visible' | 'scroll' | 'hidden';

export interface ModalLayout {
  mode: ModalLayoutMode;
  overflowBehavior: ModalLayoutOverflowBehavior;
  fullbleed: boolean;
  showCloseButton: boolean;
}

export interface OpenModalArgs {
  telemetry: ReturnType<typeof useTelemetry>;
  telemetryContext?: TelemetryContext;
  action: OpenModalAction;
}

export interface ModalState {
  isModalOpen: boolean;
  openModal: (args: OpenModalArgs) => void;
  closeModal: () => void;
  modalBoundary: ModalBoundary;
  setModalBoundary: (boundary: ModalBoundary) => void;
  layout: ModalLayout;
  setLayout: (layout: ModalLayout) => void;
  onModalEvent: (boundaryName: string, eventHandler: ModalEventHandler) => () => void;
}

const emptyModalAction: OpenModalAction = {
  content: {
    body: null,
  },
} as const;

const defaultModalLayout: ModalLayout = {
  mode: 'fit',
  overflowBehavior: 'scroll',
  fullbleed: false,
  showCloseButton: true,
} as const;

const defaultModalBoundary: SetOptional<ModalBoundary, 'telemetry'> = {
  name: 'Modal',
} as const;

const ModalContext = createContext<ModalState | null>(null);

interface ModalProviderChildrenArgs {
  modal: ReactNode;
  setLayout: (layout: ModalLayout) => void;
  isModalOpen: boolean;
}

interface ModalProviderProps {
  children: (args: ModalProviderChildrenArgs) => ReactNode;
  defaultLayout?: ModalLayout;
}

export const ModalProvider = ({
  children,
  defaultLayout = defaultModalLayout,
}: ModalProviderProps) => {
  const providerTelemetry = useTelemetry();
  const [openModalAction, setOpenModalAction] = useState<OpenModalAction>(emptyModalAction);
  const [modalBoundary, setModalBoundary] = useState<ModalBoundary>({
    ...defaultModalBoundary,
    telemetry: providerTelemetry,
  });
  const [layout, setLayout] = useState<ModalLayout>(defaultLayout);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const loadMetric = useOverlayLoadMetric(TIMER_MODAL_LOAD_TIME);

  // Holds the id of the pending post-close reset timer (see closeModal) so it can be
  // cancelled if a new open/close cycle starts, or the provider unmounts, before it fires.
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const eventRegistrations: Record<string, Array<ModalEventHandler>> = useMemo(() => ({}), []);

  const openModal: ModalState['openModal'] = useCallback(
    ({ telemetry, action }) => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      telemetry.trackInteraction('modal_open', {
        ...modalBoundary.context,
        modalName: modalBoundary.name,
        withinModal: true,
        ...action.telemetryContext,
      });
      loadMetric.beginTracking({
        startedAt: action.loadStartedAt ?? Date.now(),
        trigger: action.loadTrigger ?? 'user',
        telemetry,
        context: {
          // `modalBoundary` still describes the previous open at this point — the incoming
          // boundary is named in the action's telemetry context.
          overlayName: action.telemetryContext?.modalName ?? modalBoundary.name,
          ...pick(action.telemetryContext ?? {}, MODAL_RESOURCE_TELEMETRY_CONTEXT_KEYS),
        },
      });
      setIsModalOpen(true);
      setOpenModalAction(action);
    },
    [modalBoundary, loadMetric],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: handleEvent intentionally omitted to keep closeModal stable across renders
  const closeModal: ModalState['closeModal'] = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    handleEvent('close');
    modalBoundary.telemetry.trackInteraction('modal_close', {
      ...modalBoundary.context,
      modalName: modalBoundary.name,
      withinModal: true,
      ...openModalAction.telemetryContext,
    });
    loadMetric.handleClose();
    setIsModalOpen(false);
    closeTimeoutRef.current = setTimeout(() => {
      setOpenModalAction(emptyModalAction);
      setModalBoundary({ ...defaultModalBoundary, telemetry: providerTelemetry });
      setLayout(defaultModalLayout);
      closeTimeoutRef.current = null;
    }, 300);
  }, [providerTelemetry, modalBoundary, openModalAction, loadMetric]);

  // Cancels the pending post-close reset if the provider unmounts mid-animation, so the
  // timer never fires its setters against an unmounted tree.
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: No explanation was provided. Please be a good citizen and add one!
  const state: ModalState = useMemo(
    () => ({
      openModal,
      closeModal,
      isModalOpen,
      modalBoundary,
      setModalBoundary,
      layout,
      setLayout,
      onModalEvent: (boundaryName, eventHandler) => {
        if (!Array.isArray(eventRegistrations[boundaryName])) {
          eventRegistrations[boundaryName] = [];
        }

        eventRegistrations[boundaryName].push(eventHandler);

        return () => {
          eventRegistrations[boundaryName] = eventRegistrations[boundaryName].filter(
            (handler) => handler !== eventHandler,
          );
        };
      },
    }),
    [modalBoundary, closeModal, isModalOpen, openModal, layout],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: No explanation was provided. Please be a good citizen and add one!
  const handleEvent = useCallback(
    (eventType: ModalEventType) => {
      if (eventType === 'afterOpen') {
        loadMetric.handleAfterOpen();
      }

      openModalAction.onModalEvent?.(eventType);

      if (Array.isArray(eventRegistrations[modalBoundary.name])) {
        eventRegistrations[modalBoundary.name].forEach((eventHandler) => {
          const invokeEventHandler = async () => eventHandler(eventType);
          void invokeEventHandler();
        });
      }
    },
    [modalBoundary, openModalAction],
  );

  const modal = useMemo(
    () => (
      <ErrorBoundary boundaryName={modalBoundary.name} initialContext={modalBoundary.context}>
        {() => (
          <LoadTrackingProvider {...loadMetric.loadTrackingProps}>
            <Modal
              layout={layout}
              isOpen={isModalOpen}
              onOpen={() => handleEvent('open')}
              onAfterOpen={() => handleEvent('afterOpen')}
              close={closeModal}
              onAfterClose={() => handleEvent('afterClose')}
              {...openModalAction}
            />
          </LoadTrackingProvider>
        )}
      </ErrorBoundary>
    ),
    [modalBoundary, closeModal, handleEvent, isModalOpen, openModalAction, layout, loadMetric],
  );

  return (
    <ModalContext.Provider value={state}>
      {children({
        modal,
        isModalOpen,
        setLayout,
      })}
    </ModalContext.Provider>
  );
};

export interface UseModalOptions {
  boundaryName: string;
  layout?: ModalLayout;
  telemetryContext?: TelemetryContext;
  onModalEvent?: (eventType: ModalEventType) => void;
}

export const useModal = ({
  boundaryName,
  layout,
  telemetryContext,
  onModalEvent,
}: UseModalOptions) => {
  const hookTelemetry = useTelemetry();
  const modalContext = useContext(ModalContext);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Don't re-render when telemetry changes
  const modal: ModalState | null = useMemo(() => {
    if (!modalContext) {
      return null;
    }

    return {
      ...modalContext,
      openModal: ({
        telemetry,
        telemetryContext: openModalTelemetryContext,
        action,
      }: OpenModalArgs) => {
        modalContext.setModalBoundary({
          name: boundaryName,
          telemetry,
          context: {
            ...hookTelemetry.getScopedContextProperties(),
            ...openModalTelemetryContext,
            modalName: boundaryName,
            withinModal: true,
            ...action.telemetryContext,
            ...openModalTelemetryContext,
          },
        });

        if (layout) {
          modalContext.setLayout(layout);
        }

        modalContext.openModal({
          telemetry,
          action: {
            ...action,
            telemetryContext: {
              ...hookTelemetry.getScopedContextProperties(),
              ...openModalTelemetryContext,
              modalName: boundaryName,
              withinModal: true,
              ...action.telemetryContext,
            },
          },
        });
      },
    };
  }, [boundaryName, telemetryContext, modalContext, layout]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: No explanation was provided. Please be a good citizen and add one!
  useLayoutEffect(
    () =>
      modal && onModalEvent
        ? modal.onModalEvent(boundaryName, onModalEvent)
        : () => {
            /* noop */
          },
    [boundaryName, onModalEvent],
  );

  if (!modal) {
    throw new Error('useModal must be used within a ModalProvider');
  }

  return modal;
};

interface PublicModalState
  extends Pick<ModalState, 'isModalOpen' | 'layout' | 'setLayout' | 'closeModal'> {
  boundary: ModalBoundary;
  isProvided: boolean;
}

export const useModalState = (): PublicModalState => {
  const telemetry = useTelemetry();
  const modalState = useContext(ModalContext);

  return {
    isModalOpen: modalState?.isModalOpen ?? false,
    boundary: modalState?.modalBoundary ?? { ...defaultModalBoundary, telemetry },
    layout: modalState?.layout ?? defaultModalLayout,
    setLayout: modalState?.setLayout ?? (() => {}),
    closeModal: modalState?.closeModal ?? (() => {}),
    isProvided: modalState !== null,
  };
};
